import { guessTimeZone } from '@takeshape/util';
import { featureDefaultTimezone } from 'app/config';
import { createSelector } from 'reselect';
import type { ImmutableMap, ImmutableRecord } from '../common/utils/immutable-types';
import { NAME } from './constants';
import type { BillingStatus, ProjectSlice, ProjectState } from './types';
import { createProjectPath } from './utils';

export function isDefined<T>(x: T | null | undefined): x is T {
  return x !== null && x !== undefined;
}

export const getProjectState = (state: ProjectSlice) => state[NAME] as unknown as ImmutableRecord<ProjectState>;

export const getData = createSelector(getProjectState, (state) => state.get('data'));

export const getCurrentProjectId = createSelector(getProjectState, (domainState) => domainState.currentId);

export const getProjectPath = createSelector(getCurrentProjectId, (projectId) => createProjectPath(projectId));

export const selectProjectList = createSelector(getProjectState, (domainState) => {
  const status = {
    loading: domainState.listLoading,
    loaded: domainState.listLoaded,
    error: domainState.listError,
    items: []
  };

  if (!domainState.list) {
    return status;
  }

  const items = domainState.list
    .filter(isDefined)
    .sortBy((project) => project!.get('name'))
    .toJS();
  return {
    ...status,
    items
  };
});

function descendingComparator(a: string, b: string): number {
  if (a < b) {
    return 1;
  }

  if (a > b) {
    return -1;
  }

  return 0;
}

export const selectProjectMembers = createSelector(getProjectState, (domainState) => {
  const status = {
    loading: domainState.membersLoading,
    loaded: domainState.membersLoaded,
    error: domainState.membersError
  };

  if (!domainState.members) {
    return status;
  }

  const items = domainState.members
    .toSeq()
    .map((member) => member!.set('status', member!.get('id') === 'invited' ? 'Awaiting response' : 'Member').toJS())
    .sortBy((member) => member!.status!, descendingComparator)
    .toArray();

  return {
    ...status,
    items
  };
});

export const selectProjectAdmins = createSelector(getProjectState, (domainState) => domainState.admins);

export const getDefaultTimezone = createSelector(getData, (project) => {
  return featureDefaultTimezone === 'true' && project ? project.get('defaultTimezone') : guessTimeZone();
});

export const getProjectTimezoneWithoutDefault = createSelector(getData, (project) => {
  return featureDefaultTimezone === 'true' && project ? project.get('defaultTimezone') : undefined;
});

export const getProjectBilling = createSelector(getData, (projectData) =>
  projectData ? projectData.getIn<ImmutableMap<BillingStatus>>(['status', 'billing'])! : null
);

export const getProjectEntitlements = createSelector(getProjectBilling, (projectData) => {
  if (projectData) {
    const entitlements = projectData.get('entitlements');
    if (entitlements) {
      return entitlements.toJS();
    }
  }

  return null;
});

export const getEntitlement = (_: unknown, props: { entitlement: string }): string => props.entitlement;

export const getProjectEntitlement = createSelector(
  getProjectBilling,
  getEntitlement,
  (billing: any, entitlement): null | boolean | number | string[] => {
    const value = billing?.getIn(['entitlements', entitlement]);
    return value !== undefined ? (typeof value === 'object' && 'toJS' in value ? value.toJS() : value) : null;
  }
);

export const getProjectNumberEntitlement = createSelector(
  getEntitlement,
  getProjectEntitlement,
  (name, entitlement) => {
    if (entitlement !== null && typeof entitlement !== 'number') {
      throw new Error(`Invalid number entitlement ${name}`);
    }

    return entitlement;
  }
);
export const getProjectJS = createSelector(getData, (project) => project?.toJS());
